import { getCurrentApi } from './api';
import { Zone, CreateZoneRequest, AudioDevice, MusicFolderNode } from '../models/Zone';
import { BlockedTrack } from '../models/BlockedTrack';
import { auditService } from './auditService';

// Create a new service with "newzones" endpoints instead of "zones"
// This way we can switch between the old and new API easily

export const getZones = async (): Promise<Zone[]> => {
  const api = getCurrentApi(); const response = await api.get<Zone[]>('/newzones');
  return response.data;
};

export const getZonesByHotel = async (hotelId: number): Promise<Zone[]> => {
  const api = getCurrentApi(); const response = await api.get<Zone[]>(`/newzones/hotel/${hotelId}`);
  return response.data;
};

export const getZoneById = async (id: number): Promise<Zone> => {
  const api = getCurrentApi(); const response = await api.get<Zone>(`/newzones/${id}`);
  return response.data;
};

export const createZone = async (zone: CreateZoneRequest): Promise<Zone> => {
  try {
    // Validate required fields
    if (!zone.name) {
      throw new Error('Zone name is required');
    }
    
    if (!zone.hotelId) {
      throw new Error('Hotel ID is required');
    }
    
    // Before making the request, ensure all required properties exist
    const sanitizedZone = {
      ...zone,
      // Ensure primary music folder path is set
      musicFolderPath: zone.musicFolderPath || (zone.musicFolderPaths && zone.musicFolderPaths.length > 0 ? zone.musicFolderPaths[0] : ''),
      // Ensure musicFolderPaths is an array and not null
      musicFolderPaths: zone.musicFolderPaths || [],
      // Ensure outputChannel has a value
      outputChannel: zone.outputChannel || 'Both',
      // Ensure deviceName has a value
      deviceName: zone.deviceName || 'Default Audio Device'
    };

    // Log the request for debugging
    console.log('Creating zone with data:', sanitizedZone);
    
    // Calculate timeout based on operation complexity
    const hasMultipleFolders = sanitizedZone.musicFolderPaths.length > 2;
    const timeout = hasMultipleFolders ? 60000 : 30000; // 60 seconds for complex operations, 30 for simple ones
    
    // Add a longer timeout for this operation
    const api = getCurrentApi(); 
    const response = await api.post<Zone>('/newzones', sanitizedZone, {
      timeout: timeout
    });
    
    // Log the action to Firebase
    await auditService.logAction('Zone Created', {
      zoneId: response.data.id,
      zoneName: response.data.name,
      additionalInfo: `Created zone: ${response.data.name} with device: ${response.data.deviceName}`
    });
    
    return response.data;
  } catch (error: any) {
    // Enhanced error logging
    console.error(`Error creating zone:`, error);
    
    // Log additional details if available
    if (error.response) {
      console.error('Server responded with status:', error.response.status);
      console.error('Response data:', error.response.data);
    } else if (error.request) {
      console.error('No response received:', error.request);
    }
    
    // Construct a more detailed error message
    let errorMessage = 'Failed to create zone. ';
    
    // Handle specific HTTP status codes with more user-friendly messages
    if (error.response) {
      const status = error.response.status;
      
      if (status === 400) {
        errorMessage = 'Invalid zone data. Please check your inputs and try again.';
      } else if (status === 403) {
        errorMessage = 'You do not have permission to create zones for this hotel.';
      } else if (status === 404) {
        errorMessage = 'The specified hotel could not be found.';
      } else if (status === 500) {
        errorMessage = 'The server encountered an error while creating the zone. Please try again later.';
      }
      
      // Add specific error details if available
      if (error.response.data && typeof error.response.data === 'object') {
        if (error.response.data.message) {
          errorMessage += ' ' + error.response.data.message;
        } else if (error.response.data.error) {
          errorMessage += ' ' + error.response.data.error;
        }
      } else if (error.response.data && typeof error.response.data === 'string') {
        errorMessage += ' ' + error.response.data;
      }
    } else if (error.message) {
      // Handle timeout errors specifically
      if (error.message.includes('timeout')) {
        errorMessage = 'The request timed out. This may happen when processing large music directories or when the server is under heavy load.';
      } else {
        errorMessage += error.message;
      }
    } else {
      errorMessage += 'Unknown error occurred';
    }
    
    // Throw a properly formatted error to prevent rendering objects directly
    throw new Error(errorMessage);
  }
};

export const deleteZone = async (id: number, zoneName?: string): Promise<{ success: boolean, message?: string }> => {
  try {
    const api = getCurrentApi(); 
    await api.delete(`/newzones/${id}`);
    
    // Log the action to Firebase
    await auditService.logAction('Zone Deleted', {
      zoneId: id,
      zoneName: zoneName || `Zone ${id}`,
      additionalInfo: `Deleted zone: ${zoneName || `Zone ${id}`}`
    });
    
    return { success: true };
  } catch (error: any) {
    console.error(`Error deleting zone ${id}:`, error);
    
    // Return an error object with details from the server when available
    return {
      success: false,
      message: error.response?.data?.message || error.message || `Failed to delete zone ${id}`
    };
  }
};

export const playZone = async (id: number, zoneName?: string): Promise<{ success: boolean, message?: string }> => {
  try {
    const api = getCurrentApi(); 
    const response = await api.post<{ success: boolean, message?: string }>(`/newzones/${id}/play`);
    
    // Log the action to Firebase
    if (response.data.success) {
      await auditService.logAction('Zone Started', {
        zoneId: id,
        zoneName: zoneName || `Zone ${id}`,
        additionalInfo: 'Zone playback started'
      });
    }
    
    return response.data;
  } catch (error: any) {
    console.error(`Error playing zone ${id}:`, error);
    // Handle errors gracefully
    return { 
      success: false, 
      message: error.response?.data?.message || error.message || 'Failed to play zone'
    };
  }
};

export const stopZone = async (id: number, zoneName?: string): Promise<{ success: boolean, message?: string }> => {
  try {
    const api = getCurrentApi(); 
    const response = await api.post<{ success: boolean, message?: string }>(`/newzones/${id}/stop`);
    
    // Log the action to Firebase
    if (response.data.success) {
      await auditService.logAction('Zone Stopped', {
        zoneId: id,
        zoneName: zoneName || `Zone ${id}`,
        additionalInfo: 'Zone playback stopped'
      });
    }
    
    return response.data;
  } catch (error: any) {
    console.error(`Error stopping zone ${id}:`, error);
    // Handle errors gracefully
    return { 
      success: false, 
      message: error.response?.data?.message || error.message || 'Failed to stop zone'
    };
  }
};

export const playPreviousTrack = async (id: number, zoneName?: string): Promise<{ success: boolean, message?: string }> => {
  try {
    const api = getCurrentApi(); 
    const response = await api.post<{ success: boolean, message?: string }>(`/newzones/${id}/previous`);
    
    // Log the action to Firebase
    if (response.data.success) {
      await auditService.logAction('Previous Track', {
        zoneId: id,
        zoneName: zoneName || `Zone ${id}`,
        additionalInfo: 'Played previous track'
      });
    }
    
    return response.data;
  } catch (error: any) {
    console.error(`Error playing previous track for zone ${id}:`, error);
    // Convert error to our standard format
    return { 
      success: false, 
      message: error.response?.data?.message || error.message || 'Failed to play previous track'
    };
  }
};

export const playNextTrack = async (id: number, zoneName?: string): Promise<{ success: boolean, message?: string }> => {
  try {
    const api = getCurrentApi(); 
    const response = await api.post<{ success: boolean, message?: string }>(`/newzones/${id}/next`);
    
    // Log the action to Firebase
    if (response.data.success) {
      await auditService.logAction('Next Track', {
        zoneId: id,
        zoneName: zoneName || `Zone ${id}`,
        additionalInfo: 'Played next track'
      });
    }
    
    return response.data;
  } catch (error: any) {
    console.error(`Error playing next track for zone ${id}:`, error);
    // Convert error to our standard format
    return { 
      success: false, 
      message: error.response?.data?.message || error.message || 'Failed to play next track'
    };
  }
};

export const setVolume = async (id: number, volume: number, zoneName?: string): Promise<{ success: boolean, message?: string, volume?: number }> => {
  try {
    const api = getCurrentApi(); 
    const response = await api.post<{ success: boolean, message?: string, volume?: number }>(`/newzones/${id}/volume`, volume);
    
    // Log the action to Firebase
    if (response.data.success) {
      await auditService.logAction('Volume Changed', {
        zoneId: id,
        zoneName: zoneName || `Zone ${id}`,
        newValue: `Volume: ${volume}`,
        additionalInfo: `Volume set to ${volume}`
      });
    }
    
    return response.data;
  } catch (error: any) {
    console.error(`Error setting volume for zone ${id}:`, error);
    // Handle errors gracefully
    return { 
      success: false, 
      message: error.response?.data?.message || error.message || 'Failed to set volume'
    };
  }
};

export const setShuffle = async (id: number, shuffle: boolean): Promise<{ success: boolean, shuffle?: boolean }> => {
  try {
    const api = getCurrentApi();
    const response = await api.post<{ success: boolean, shuffle?: boolean }>(`/newzones/${id}/shuffle`, shuffle);
    return response.data;
  } catch (error: any) {
    console.error(`Error setting shuffle for zone ${id}:`, error);
    return { success: false };
  }
};

/**
 * Blocks the currently playing track in a zone
 * @param id Zone ID
 * @returns Object with success status and the blocked track details
 */
export const blockCurrentTrack = async (id: number, zoneName?: string): Promise<{ 
  success: boolean, 
  blockedTrack?: BlockedTrack, 
  message?: string
}> => {
  try {
    // Add timeout and better error handling
    const api = getCurrentApi(); 
    const response = await api.post<{ 
      success: boolean, 
      blockedTrack: BlockedTrack, 
      message?: string 
    }>(`/newzones/${id}/block`, null, { timeout: 15000 });
    
    // Log the action to Firebase
    if (response.data.success) {
      await auditService.logAction('Track Blocked', {
        zoneId: id,
        zoneName: zoneName || `Zone ${id}`,
        additionalInfo: `Blocked track: ${response.data.blockedTrack?.title || 'Current track'}`
      });
    }
    
    console.log(`Successfully blocked track for zone ${id}`);
    return response.data;
  } catch (error: any) {
    // Enhanced error logging
    console.error(`Error blocking current track for zone ${id}:`, error);
    
    if (error.response) {
      // Server responded with error status
      console.error(`Error status: ${error.response.status}`);
      console.error(`Error data:`, error.response.data);
      
      return { 
        success: false, 
        message: error.response?.data?.message || `Server error (${error.response.status}): Failed to block track`
      };
    } else if (error.request) {
      // No response received (network error)
      console.error(`No response received:`, error.request);
      return { 
        success: false, 
        message: 'Network error: Server did not respond. Please try again.'
      };
    } else {
      // Other errors
      return { 
        success: false, 
        message: error.message || 'Failed to block current track'
      };
    }
  }
};

// Channel Mapping API functions

export interface ZoneChannelMapping {
  id?: number;
  zoneId?: number;
  deviceChannelIndex: number;
  volume: number;
  isActive: boolean;
  channelName?: string;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Gets channel mappings for a specific zone
 */
export const getZoneChannelMappings = async (zoneId: number): Promise<ZoneChannelMapping[]> => {
  try {
    const api = getCurrentApi();
    const response = await api.get<ZoneChannelMapping[]>(`/newzones/${zoneId}/channel-mappings`);
    return response.data;
  } catch (error: any) {
    console.error(`Error getting channel mappings for zone ${zoneId}:`, error);
    throw error;
  }
};

/**
 * Sets channel mappings for a specific zone
 */
export const setZoneChannelMappings = async (
  zoneId: number, 
  mappings: Omit<ZoneChannelMapping, 'id' | 'zoneId' | 'createdAt' | 'updatedAt'>[]
): Promise<ZoneChannelMapping[]> => {
  try {
    const api = getCurrentApi();
    const response = await api.post<ZoneChannelMapping[]>(
      `/newzones/${zoneId}/channel-mappings`,
      mappings,
      { timeout: 10000 }
    );
    return response.data;
  } catch (error: any) {
    console.error(`Error setting channel mappings for zone ${zoneId}:`, error);
    throw error;
  }
};

/**
 * Updates a specific channel mapping for a zone
 */
export const updateChannelMapping = async (
  zoneId: number,
  mappingId: number,
  mapping: Partial<Omit<ZoneChannelMapping, 'id' | 'zoneId' | 'createdAt' | 'updatedAt'>>
): Promise<ZoneChannelMapping> => {
  try {
    const api = getCurrentApi();
    const response = await api.put<ZoneChannelMapping>(
      `/newzones/${zoneId}/channel-mappings/${mappingId}`,
      mapping,
      { timeout: 10000 }
    );
    return response.data;
  } catch (error: any) {
    console.error(`Error updating channel mapping ${mappingId} for zone ${zoneId}:`, error);
    throw error;
  }
};

/**
 * Gets detailed information about a specific audio device including channel details
 */
export const getAudioDeviceDetails = async (deviceId: string): Promise<AudioDevice> => {
  try {
    const api = getCurrentApi();
    const response = await api.get<AudioDevice>(`/audiodevices/${encodeURIComponent(deviceId)}`);
    return response.data;
  } catch (error: any) {
    console.error(`Error getting audio device details for ${deviceId}:`, error);
    throw error;
  }
};

/**
 * Gets channel information for a specific audio device
 */
export const getDeviceChannels = async (deviceId: string): Promise<any[]> => {
  try {
    const api = getCurrentApi();
    const response = await api.get<any[]>(`/audiodevices/${encodeURIComponent(deviceId)}/channels`);
    return response.data;
  } catch (error: any) {
    console.error(`Error getting device channels for ${deviceId}:`, error);
    throw error;
  }
};

// Alias for blockCurrentTrack for backward compatibility
export const toggleBlockMusic = async (id: number, zoneName?: string): Promise<{ 
  success: boolean, 
  blockedTrack?: BlockedTrack, 
  message?: string 
}> => {
  return blockCurrentTrack(id, zoneName);
};

/**
 * Gets all blocked tracks for a given hotel
 * @param hotelId Hotel ID to get blocked tracks for
 * @returns Array of blocked tracks
 * @deprecated Use getBlockedTracksForZone instead
 */
export const getBlockedTracks = async (hotelId: number): Promise<BlockedTrack[]> => {
  try {
    // Add timeout of 10 seconds to prevent long-hanging requests
    const api = getCurrentApi(); const response = await api.get<BlockedTrack[]>(
      `/newzones/hotel/${hotelId}/blocked-tracks`, 
      { timeout: 10000 }
    );
    
    console.log(`Successfully fetched ${response.data.length} blocked tracks for hotel ${hotelId}`);
    return response.data;
  } catch (error: any) {
    // Log more detailed error information
    console.error(`Error fetching blocked tracks for hotel ${hotelId}:`, error);
    
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      console.error(`Error status: ${error.response.status}`);
      console.error(`Error data:`, error.response.data);
    } else if (error.request) {
      // The request was made but no response was received
      console.error(`No response received:`, error.request);
    } 
    
    // Always return an empty array instead of failing
    return [];
  }
};

/**
 * Gets blocked tracks for a specific zone
 * @param zoneId Zone ID to get blocked tracks for
 * @returns Array of blocked tracks
 */
export const getBlockedTracksForZone = async (zoneId: number): Promise<BlockedTrack[]> => {
  try {
    // Add timeout of 10 seconds to prevent long-hanging requests
    const api = getCurrentApi(); const response = await api.get<BlockedTrack[]>(
      `/newzones/${zoneId}/blocked-tracks`, 
      { timeout: 10000 }
    );
    
    console.log(`Successfully fetched ${response.data.length} blocked tracks for zone ${zoneId}`);
    return response.data;
  } catch (error: any) {
    // Log more detailed error information
    console.error(`Error fetching blocked tracks for zone ${zoneId}:`, error);
    
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      console.error(`Error status: ${error.response.status}`);
      console.error(`Error data:`, error.response.data);
    } else if (error.request) {
      // The request was made but no response was received
      console.error(`No response received:`, error.request);
    } 
    
    // Always return an empty array instead of failing
    return [];
  }
};

/**
 * Unblocks a previously blocked track
 * @param id The ID of the blocked track to unblock
 * @returns Success status
 */
export const unblockTrack = async (id: number): Promise<{ success: boolean, message?: string }> => {
  try {
    const api = getCurrentApi(); 
    const response = await api.delete<{ success: boolean, message?: string }>(`/newzones/blocked-tracks/${id}`);
    
    // Log the action to Firebase
    if (response.data.success) {
      await auditService.logAction('Track Unblocked', {
        additionalInfo: `Unblocked track ID: ${id}`
      });
    }
    
    return response.data;
  } catch (error: any) {
    console.error(`Error unblocking track ${id}:`, error);
    return { 
      success: false, 
      message: error.response?.data?.message || error.message || 'Failed to unblock track'
    };
  }
};

export const getAudioDevices = async (skipAsioAnalysis: boolean = false): Promise<AudioDevice[]> => {
  try {
    const api = getCurrentApi();
    
    // Add skipAsioAnalysis parameter to prevent ASIO driver conflicts during modal dialogs
    const queryParam = skipAsioAnalysis ? '?skipAsioAnalysis=true' : '';
    
    // First try the comprehensive analysis endpoint
    try {
      const response = await api.get<AudioDevice[]>(`/audiodevices/analyze${queryParam}`);
      if (skipAsioAnalysis) {
        console.log('Modal-safe audio device analysis successful (no ASIO conflicts):', response.data);
      } else {
        console.log('Comprehensive audio device analysis successful:', response.data);
      }
      return response.data;
    } catch (analysisError) {
      console.warn('Comprehensive analysis failed, falling back to standard endpoint:', analysisError);
      
      // Fallback to standard endpoint
      const response = await api.get<AudioDevice[]>(`/audiodevices${queryParam}`);
      return response.data;
    }
  } catch (error) {
    console.error('Error fetching audio devices:', error);
    // Return fallback devices when API fails
    return [
      { 
        name: 'Default Audio Device', 
        channels: ['Left', 'Right', 'Both'],
        outputChannelCount: 2,
        deviceType: 'Windows Audio',
        supportsIndependentChannels: false
      },
      { 
        name: 'Speakers (High Definition Audio)', 
        channels: ['Left', 'Right', 'Both'],
        outputChannelCount: 2,
        deviceType: 'Windows Audio',
        supportsIndependentChannels: false
      }
    ];
  }
};

/**
 * Forces a refresh of audio device detection
 */
export const refreshAudioDevices = async (): Promise<{ success: boolean, deviceCount?: number, message?: string }> => {
  try {
    const api = getCurrentApi();
    const response = await api.post<{ 
      success: boolean, 
      deviceCount: number, 
      message: string,
      devices: any[]
    }>('/audiodevices/refresh');
    
    console.log('Audio device refresh response:', response.data);
    return {
      success: response.data.success,
      deviceCount: response.data.deviceCount,
      message: response.data.message
    };
  } catch (error: any) {
    console.error('Error refreshing audio devices:', error);
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Failed to refresh audio devices'
    };
  }
};

/**
 * Gets detailed analysis for a specific audio device
 */
export const analyzeSpecificDevice = async (deviceId: string): Promise<any> => {
  try {
    const api = getCurrentApi();
    const response = await api.get(`/audiodevices/${encodeURIComponent(deviceId)}/analyze`);
    return response.data;
  } catch (error: any) {
    console.error(`Error analyzing device ${deviceId}:`, error);
    throw error;
  }
};

/**
 * Gets all available ASIO drivers and their capabilities
 */
export const getAsioDevices = async (skipAsioAnalysis: boolean = false): Promise<AudioDevice[]> => {
  try {
    const api = getCurrentApi();
    
    // Add skipAsioAnalysis parameter to prevent ASIO driver conflicts during modal dialogs
    const queryParam = skipAsioAnalysis ? '?skipAsioAnalysis=true' : '';
    
    const response = await api.get<AudioDevice[]>(`/audiodevices/asio${queryParam}`);
    if (skipAsioAnalysis) {
      console.log('Modal-safe ASIO devices retrieved (no driver instantiation):', response.data);
    } else {
      console.log('ASIO devices retrieved:', response.data);
    }
    return response.data;
  } catch (error: any) {
    console.error('Error fetching ASIO devices:', error);
    throw error;
  }
};

/**
 * Tests ASIO audio output with a specific driver
 */
export const testAsioDriver = async (driverName: string, outputChannels: number[]): Promise<{
  success: boolean;
  message: string;
  driverName?: string;
  channelsTested?: number[];
}> => {
  try {
    const api = getCurrentApi();
    const response = await api.post(`/audiodevices/asio/${encodeURIComponent(driverName)}/test`, {
      outputChannels,
      durationSeconds: 2
    });
    return response.data;
  } catch (error: any) {
    console.error(`Error testing ASIO driver ${driverName}:`, error);
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Failed to test ASIO driver'
    };
  }
};

export const getMusicFolders = async (skipAsioAnalysis: boolean = false): Promise<string[]> => {
  try {
    // Note: This still uses the old API endpoint
    const api = getCurrentApi(); 
    
    // Add skipAsioAnalysis parameter to prevent ASIO driver conflicts during modal dialogs
    const queryParam = skipAsioAnalysis ? '?skipAsioAnalysis=true' : '';
    
    const response = await api.get<string[]>(`/audiodevices/music-folders${queryParam}`);
    if (skipAsioAnalysis) {
      console.log('Modal-safe music folders retrieved (no ASIO conflicts):', response.data);
    }
    return response.data;
  } catch (error) {
    console.error('Error fetching music folders:', error);
    // Return fallback folders when API fails
    return ['Music', 'MusicCollection', 'Tracks'];
  }
};

export const getMusicSubfolders = async (parentFolder: string, skipAsioAnalysis: boolean = false): Promise<string[]> => {
  try {
    // Note: This still uses the old API endpoint
    const api = getCurrentApi(); 
    
    // Add skipAsioAnalysis parameter to prevent ASIO driver conflicts during modal dialogs
    const queryParam = skipAsioAnalysis ? '?skipAsioAnalysis=true' : '';
    
    const response = await api.get<string[]>(`/audiodevices/music-folders/${encodeURIComponent(parentFolder)}/subfolders${queryParam}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching subfolders for ${parentFolder}:`, error);
    // Return empty array on failure
    return [];
  }
};

export const getMusicFoldersTree = async (skipAsioAnalysis: boolean = false): Promise<MusicFolderNode[]> => {
  try {
    // Note: This still uses the old API endpoint
    const api = getCurrentApi(); 
    
    // Add skipAsioAnalysis parameter to prevent ASIO driver conflicts during modal dialogs
    const queryParam = skipAsioAnalysis ? '?skipAsioAnalysis=true' : '';
    
    const response = await api.get<MusicFolderNode[]>(`/audiodevices/music-folders-tree${queryParam}`);
    if (skipAsioAnalysis) {
      console.log('Modal-safe music folders tree retrieved (no ASIO conflicts):', response.data);
    }
    return response.data;
  } catch (error) {
    console.error('Error fetching music folders tree:', error);
    // Return empty array on failure
    return [];
  }
};

export const updateZone = async (id: number, zoneData: Partial<CreateZoneRequest>): Promise<Zone> => {
  try {
    // Use PUT method directly
    const api = getCurrentApi(); 
    const response = await api.put<Zone>(`/newzones/${id}`, zoneData);
    
    // Log the action to Firebase
    await auditService.logAction('Zone Updated', {
      zoneId: id,
      zoneName: response.data.name,
      additionalInfo: `Updated zone: ${response.data.name}`,
      newValue: `Device: ${response.data.deviceName}, Volume: ${response.data.volume}`
    });
    
    return response.data;
  } catch (error: any) {
    console.error(`Error updating zone ${id}:`, error);
    // Throw a properly formatted error to prevent rendering objects directly
    throw new Error(error.response?.data?.message || error.message || `Failed to update zone ${id}`);
  }
};

export const getUpcomingTracks = async (id: number, count: number = 5): Promise<any[]> => {
  try {
    const api = getCurrentApi(); const response = await api.get<any[]>(`/newzones/${id}/upcoming-tracks?count=${count}`);
    return response.data;
  } catch (error: any) {
    console.error(`Error fetching upcoming tracks for zone ${id}:`, error);
    // Return empty array on error
    return [];
  }
};

export const refreshPlaylist = async (id: number, zoneName?: string): Promise<{ success: boolean, trackCount?: number, message?: string }> => {
  try {
    const api = getCurrentApi(); 
    const response = await api.post<{ success: boolean, trackCount?: number, message?: string }>(`/newzones/${id}/refresh-playlist`);
    
    // Log the action to Firebase
    if (response.data.success) {
      await auditService.logAction('Playlist Refreshed', {
        zoneId: id,
        zoneName: zoneName || `Zone ${id}`,
        additionalInfo: `Refreshed playlist for zone: ${zoneName || `Zone ${id}`}${response.data.trackCount ? `, ${response.data.trackCount} tracks loaded` : ''}`
      });
    }
    
    return response.data;
  } catch (error: any) {
    console.error(`Error refreshing playlist for zone ${id}:`, error);
    // Handle errors gracefully
    return { 
      success: false, 
      message: error.response?.data?.message || error.message || 'Failed to refresh playlist'
    };
  }
};

// Real-time progress tracking API
export interface PlaybackProgress {
  success: boolean;
  zoneId: number;
  isPlaying: boolean;
  progressPercent: number;
  currentTimeSeconds: number;
  totalTimeSeconds: number;
  currentTimeFormatted: string;
  totalTimeFormatted: string;
  volume: number; // Database'den gelen güncel volume
}

export const getPlaybackProgress = async (id: number): Promise<PlaybackProgress | null> => {
  try {
    const api = getCurrentApi(); 
    const response = await api.get<PlaybackProgress>(`/newzones/${id}/progress`);
    return response.data;
  } catch (error: any) {
    console.error(`Error getting playback progress for zone ${id}:`, error);
    return null;
  }
};